/**
 * Custom hook for sending chat messages with optimistic updates
 * Integrates TanStack Query mutation with conversation caching
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useMemo } from 'react';
import { useStreamingStore } from '../stores/streamingStore';
import {
  sendChatMessage,
  SendMessageRequest,
  SendMessageResponse,
  chatUtils,
  ChatApiError
} from '../api/chat';
import { conversationQueryKeys, ConversationDetailResponse, fetchConversationDetail } from '../api/conversations';
import { ApiError } from '../api/base/errors';
import { useConversationStore } from '../stores/conversationStore';
import { useChatStore } from '../stores/chatStore';
import { GENIE_SPACE_AGENT_IDS } from '../constants/agents';
import { DEFAULT_MODEL } from '../config/defaults';
import { FEATURES } from '../config/featureFlags';
import {
  DIGITAL_MAINTENANCE_RESULT_COMMAND,
  DIGITAL_MAINTENANCE_WAIT_MS,
  isDigitalMaintenanceOnly,
  isDigitalMaintenancePendingResult,
} from '../features/chat/utils/digitalMaintenanceCheckResult';

const activeChatSends = new Set<string>();
export const isChatSendInFlight = (conversationId: string): boolean => activeChatSends.has(conversationId);

/**
 * Constants for error detection
 */
const ERROR_NAMES = {
  ABORT: 'AbortError',
} as const;

const ERROR_MESSAGES = {
  ABORTED: 'aborted',
  CANCELLED: 'cancelled',
} as const;

const ERROR_CODES = {
  REQUEST_CANCELLED: 'REQUEST_CANCELLED',
} as const;

const CONNECTION_CANCELLED_MESSAGE = 'Connection cancelled';

/**
 * Optimistic update context for rollback on error
 */
interface OptimisticUpdateContext {
  previousConversation: any;
  previousLists: any; // For conversation list rollback
  previousSelectedConversationId: string | null; // For selection rollback
  previousCurrentConversationId: string | null; // For current conversation rollback
  optimisticUserMessage: any;
  optimisticAssistantMessage: any;
  previousCheck: ReturnType<typeof useChatStore.getState>['digitalMaintenanceChecks'][string] | undefined;
}

const ensureDefaultGenieIds = (ids?: Record<string, unknown> | null): Record<string, string> => {
  const normalized: Record<string, string> = {};

  if (ids) {
    for (const [key, value] of Object.entries(ids)) {
      if (typeof value === 'string') {
        normalized[key] = value;
      } else if (value == null) {
        normalized[key] = '';
      }
    }
  }

  return normalized;
};

const getExplicitGenieSpaceSelection = (selectedAgents: string[]): string | undefined => {
  return GENIE_SPACE_AGENT_IDS.find(agentId => selectedAgents.includes(agentId));
};


/**
 * Core mutation hook for sending chat messages
 * Follows TanStack Query best practices with optimistic updates
 */
export function useSendMessage(conversationId: string, onNavigate?: () => void) {
  const queryClient = useQueryClient();
  const { endStream } = useStreamingStore();

  // Get selection state management actions from conversation store
  const setSelectedConversation = useConversationStore((state) => state.setSelectedConversation);
  const selectedConversationId = useConversationStore((state) => state.selectedConversationId);
  const currentConversationId = useConversationStore((state) => state.currentConversationId);

  // Get items from chat store
  const {
    uploadSessionId,
    clearUploadSession,
    clearAttachments,
    // Conversation file management for synchronization
    getConversationFiles,
    clearConversationUploadSession,
    // Clear pending files for message after send
    clearPendingFilesForMessage,
    getPendingFilesForMessage,
    syncConversationFiles,
    hasGenieAccess,
    allowedGenieSpaceNames
  } = useChatStore();
  const attachments = useChatStore(state => state.attachments);

  return useMutation({
    meta: { type: 'chat' },
    mutationFn: async (request: SendMessageRequest) => {
      // Use the AbortController passed in the request
      if (!request.abortController) {
        throw new Error('AbortController is required for request cancellation');
      }

      // Log mutation start
      console.log('🚀 Starting chat mutation for conversation:', request.conversationId);

      // Call the existing logic as needed
      // Get conversation history from the query cache
      const conversationData = queryClient.getQueryData<ConversationDetailResponse>(
        conversationQueryKeys.detail(conversationId)
      );

      // Transform conversation history to backend format
      let history: Array<{ user: string; bot: string }> = [];
      if (conversationData?.conversationHistory) {
        history = chatUtils.transformHistoryToBackendFormat(conversationData.conversationHistory);
      }

      // Analyze file state from multiple sources
      const isCheck = request.sendMode === 'digitalMaintenanceCheck';
      const pendingFilesForMessage = isCheck ? [] : getPendingFilesForMessage(conversationId);
      const fileState = isCheck ? null : chatUtils.analyzeFileState({
        conversationData,
        uploadSessionId,
        attachments,
        conversationFiles: getConversationFiles(conversationId),
        pendingFilesForMessage,
      });

      // Determine conversation title - only use first message for new conversations
      const conversationTitle = chatUtils.getConversationTitle(
        conversationData?.conversationHistory,
        request.message
      );

      const currentConversation = queryClient.getQueryData<ConversationDetailResponse>(conversationQueryKeys.detail(conversationId));
      const normalizedGenieIds = ensureDefaultGenieIds(currentConversation?.genie_conversation_ids as Record<string, string> | null);
      // Foundry-backed RAG agents reuse their own persisted conversation ids per specialist.
      const foundryRagConversationIds = currentConversation?.foundry_rag_conversation_ids;
      // Fallback: read conversation-scoped upload session (used only when pending files exist without a fresh session)
      const conversationUploadSessionId = useChatStore.getState().conversationFiles?.[conversationId]?.lastUploadSessionId || '';
      const effectiveUploadSessionId = isCheck ? '' :
        (fileState?.uploadSessionId || (pendingFilesForMessage.length > 0 ? conversationUploadSessionId : ''));

      // Determine which Genie spaces to query based on agent selection
      // Backend handles all permission checks
      let genieSpacesToQuery = allowedGenieSpaceNames;

      // Check if user explicitly selected a Genie space agent
      const selectedAgents = request.options?.agents || [];
      const explicitlySelectedGenieSpace = getExplicitGenieSpaceSelection(selectedAgents);
      if (explicitlySelectedGenieSpace) {
        genieSpacesToQuery = [explicitlySelectedGenieSpace];
      }

      // Add history and proper title to the request
      return sendChatMessage({
        conversationId: request.conversationId,
        message: request.message,
        abortController: request.abortController,
        history,
        currentMessageUploadedFiles: pendingFilesForMessage,
        // AbortController is already in the request object
        options: {
          ...request.options,
          conversationTitle,
          // Enhanced file integration:
          uploadSessionId: effectiveUploadSessionId,
          useFiles: fileState?.shouldUseFiles ?? false,
          uploadedFiles: fileState?.allFiles ?? [],
          hasGenieAccess,
          genieSpaceNames: genieSpacesToQuery,
          // TODO: This is currently hard coded as the saving doesnt work on the backend...
          genieSpaceConversationIds: normalizedGenieIds,
          // Send the last known Foundry ids back so each PPL-family agent can continue its own server-side thread.
          foundryRagConversationIds,
          digitalMaintenanceTarget: currentConversation?.digital_maintenance_target,
          // Only use websearch_conversation_id - old conversations start fresh with new agent
          websearchConversationId: currentConversation?.websearch_conversation_id,
        }
      });
    },

    // Optimistic updates
    onMutate: async (variables): Promise<OptimisticUpdateContext> => {
      // Cancel any outgoing refetches (conversation detail)
      await queryClient.cancelQueries({
        queryKey: conversationQueryKeys.detail(conversationId)
      });

      // Cancel conversation list queries to prevent overwrites
      await queryClient.cancelQueries({
        queryKey: conversationQueryKeys.list({})
      });

      // Snapshot previous state for rollback
      const previousConversation = queryClient.getQueryData(
        conversationQueryKeys.detail(conversationId)
      );
      const chatState = useChatStore.getState();
      const previousCheck = chatState.digitalMaintenanceChecks[conversationId];

      // Snapshot conversation lists for rollback
      const previousLists = queryClient.getQueryData(
        conversationQueryKeys.list({})
      );

      // Snapshot previous selection state for rollback
      const previousSelectedConversationId = selectedConversationId;
      const previousCurrentConversationId = currentConversationId;

      // Get pending files for optimistic display
      const pendingFiles = variables.sendMode === 'digitalMaintenanceCheck'
        ? [] : getPendingFilesForMessage(conversationId);

      // Create optimistic messages
      const optimisticUserMessage = chatUtils.createOptimisticUserMessage(
        variables.message,
        conversationId
      );

      const optimisticAssistantMessage = chatUtils.createOptimisticAssistantMessage(
        conversationId
      );

      if (FEATURES.showDigitalMaintenanceCheckResult) {
        const lastTurn = (previousConversation as ConversationDetailResponse | undefined)?.conversationHistory?.at(-1);
        const isFollowUp = isDigitalMaintenanceOnly(variables.options?.agents)
          && variables.message.trim().toLowerCase() === DIGITAL_MAINTENANCE_RESULT_COMMAND.toLowerCase()
          && previousCheck?.latestMessageId === lastTurn?.message_id
          && isDigitalMaintenancePendingResult(lastTurn?.digital_maintenance_status);
        const eligibleAtMs = isFollowUp ? previousCheck.eligibleAtMs
          : isDigitalMaintenanceOnly(variables.options?.agents)
            && variables.message.trim().toLowerCase() !== DIGITAL_MAINTENANCE_RESULT_COMMAND.toLowerCase()
            ? Date.now() + DIGITAL_MAINTENANCE_WAIT_MS : undefined;
        chatState.setDigitalMaintenanceCheck(conversationId, eligibleAtMs === undefined ? undefined : {
          eligibleAtMs,
          latestMessageId: optimisticUserMessage.id,
          buttonMessageId: variables.sendMode === 'digitalMaintenanceCheck' ? optimisticUserMessage.id : undefined,
        });
      }

      variables.options = {
        ...(variables.options || {}),
        currentStreamId: optimisticUserMessage.id,
      };

      // Optimistically update the conversation cache
      queryClient.setQueryData(
        conversationQueryKeys.detail(conversationId),
        (old: any) => {
          if (!old) return old;

          // Create new chat messages for the conversation history
          const newUserChatMessage = {
            user: variables.message,
            bot: '', // Will be filled by assistant response
            message_id: optimisticUserMessage.id,
            tools: variables.options?.tools || [],
            temperature: variables.options?.temperature || 0,
            gptVersion: variables.options?.model || DEFAULT_MODEL,
            // Add optimistic files for immediate display
            uploadedFiles: pendingFiles.length > 0 ? pendingFiles : undefined,
          };

          // Strip file_processing_status so a stale cached 'processing' value is never
          // re-propagated back into the chatStore via useConversationDetailWithStore.
          // Active processing is tracked exclusively in chatStore; this field is only
          // used for page-refresh sync and should not survive an optimistic mutation.
          const { file_processing_status: _ignored, ...oldWithout } = old;
          const updatedData = {
            ...oldWithout,
            conversationHistory: [...old.conversationHistory, newUserChatMessage],
          };

          // Trigger immediate scroll by incrementing message count
          // This will cause displayMessages.length to change in ConversationContent
          return updatedData;
        }
      );

      // Optimistic conversation list update (Context7 pattern)
      const now = new Date().toISOString();
      const isNewConversation = !previousConversation ||
        !(previousConversation && typeof previousConversation === 'object' && 'conversationHistory' in previousConversation) ||
        !Array.isArray((previousConversation as any)?.conversationHistory) ||
        (previousConversation as any)?.conversationHistory?.length === 0;

      queryClient.setQueryData(
        conversationQueryKeys.list({}),
        (oldLists: any) => {
          if (!oldLists || !Array.isArray(oldLists)) {
            return oldLists;
          }

          if (isNewConversation) {
            // Add new conversation optimistically (Fix Issue 2: Tag as optimistic)
            const optimisticConversation = {
              conversationID: conversationId,
              message_id: null, // Will be updated on success
              conversationTitle: variables.message.substring(0, 200) + (variables.message.length > 200 ? '...' : ''),
              profile: 'Chat',
              category: {},
              date_last_accesed: now,
              _optimistic: true, // Tag for merge strategy
            };

            const newList = [optimisticConversation, ...oldLists];
            return newList;
          } else {
            // Update existing conversation's timestamp and move to top
            const updatedList = oldLists
              .map((conv: any) =>
                conv.conversationID === conversationId
                  ? { ...conv, date_last_accesed: now }
                  : conv
              )
              .sort((a: any, b: any) =>
                new Date(b.date_last_accesed).getTime() - new Date(a.date_last_accesed).getTime()
              );
            return updatedList;
          }
        }
      );

      // Update selection state for new conversations (Fix Issue 1)
      if (isNewConversation) {
        setSelectedConversation(conversationId);

        // Also update current conversation ID for immediate navbar highlighting
        const setCurrentConversationId = useConversationStore.getState().setCurrentConversationId;
        setCurrentConversationId(conversationId);
      }

      return {
        previousConversation,
        previousLists, // Add for rollback
        previousSelectedConversationId, // Add for selection rollback
        previousCurrentConversationId, // Add for current conversation rollback
        optimisticUserMessage,
        optimisticAssistantMessage,
        previousCheck,
      };
    },

    // Success handling
    onSuccess: (response: SendMessageResponse, variables: SendMessageRequest, context: OptimisticUpdateContext) => {
      if (FEATURES.showDigitalMaintenanceCheckResult) {
        const state = useChatStore.getState();
        const check = state.digitalMaintenanceChecks[conversationId];
        if (check?.latestMessageId === context.optimisticUserMessage.id) {
          state.setDigitalMaintenanceCheck(conversationId, {
            ...check,
            latestMessageId: response.message_id,
            buttonMessageId: check.buttonMessageId ? response.message_id : undefined,
          });
        }
      }
      // Update cache with real response data, replacing the specific optimistic message by ID
      queryClient.setQueryData(
        conversationQueryKeys.detail(conversationId),
        (old: any) => {
          // Create the completed message pair
          const completedMessage = {
            user: variables.message,
            bot: response.answer,
            digital_maintenance_status: response.digital_maintenance_status,
            message_id: response.message_id,
            tools: variables.options?.tools || [],
            gptVersion: variables.options?.model || DEFAULT_MODEL,
            data_points: response.data_points,
            thoughts: response.thoughts,
            agent_classification: response.agent_classification,
            genie_conversation_ids: ensureDefaultGenieIds(response.genie_conversation_ids),
            // Store returned Foundry ids on the completed turn so the cache reflects the backend state immediately.
            foundry_rag_conversation_ids: response.foundry_rag_conversation_ids,
            websearch_conversation_id: response.websearch_conversation_id,
            genie_space_names: response.genie_space_names,
            genie_conversation_id: response.genie_conversation_id,
            genie_message_id: response.genie_message_id,
            toolCalls: response.toolCalls || [],
          };

          if (!old) {
            // New conversation - create initial structure
            return {
              conversationID: conversationId,
              conversationHistory: [completedMessage],
              uploadedFiles: [],
              genie_conversation_ids: ensureDefaultGenieIds(response.genie_conversation_ids),
              genie_space_conversation_IDs: ensureDefaultGenieIds(response.genie_conversation_ids),
              // New conversations need the map cached at the conversation root for the next turn to reuse it.
              foundry_rag_conversation_ids: response.foundry_rag_conversation_ids,
              digital_maintenance_target: response.digital_maintenance_target,
              // Only use websearch_conversation_id from response
              websearch_conversation_id: response.websearch_conversation_id,
            };
          }

          const optimisticId = context?.optimisticUserMessage?.id;
          let found = false;
          const updatedHistory = old.conversationHistory.map((msg: any) => {
            if (optimisticId && msg.message_id === optimisticId) {
              found = true;
              // Preserve any uploadedFiles that might have been set optimistically
              return { ...completedMessage, uploadedFiles: msg.uploadedFiles };
            }
            return msg;
          });

          const finalHistory = found
            ? updatedHistory
            : [...old.conversationHistory, completedMessage]; // Fallback: append if optimistic not found

          // Strip file_processing_status from the persisted cache entry for the same
          // reason as in onMutate: prevent a stale 'processing' value surviving a
          // successful round-trip and re-triggering the processing banner.
          const { file_processing_status: _ignoredOnSuccess, ...oldWithoutProcessing } = old;
          return {
            ...oldWithoutProcessing,
            conversationHistory: finalHistory,
            genie_conversation_ids: ensureDefaultGenieIds({
              ...old.genie_conversation_ids,
              ...response.genie_conversation_ids,
            }),
            genie_space_conversation_IDs: ensureDefaultGenieIds({
              ...old.genie_space_conversation_IDs,
              ...response.genie_conversation_ids,
            }),
            // Preserve the existing map when a response does not update Foundry-backed specialists.
            foundry_rag_conversation_ids: response.foundry_rag_conversation_ids || old.foundry_rag_conversation_ids,
            digital_maintenance_target: response.digital_maintenance_target || old.digital_maintenance_target,
            // Only use websearch_conversation_id - preserve from old if not in response
            websearch_conversation_id: response.websearch_conversation_id || old.websearch_conversation_id,
          };
        }
      );

      // Cancel any in-flight refetches that may have been triggered between
      // onMutate and onSuccess (e.g. by the upload_complete effect). Without
      // this, a stale refetch landing after our setQueryData above would
      // overwrite the correct answer with old server data (the backend saves
      // conversation data as a background task).
      queryClient.cancelQueries({
        queryKey: conversationQueryKeys.detail(conversationId),
      });

      // Navigate immediately - no verification needed
      if (onNavigate) {
        const currentPath = window.location.pathname;
        const targetPath = `/chat/${conversationId}`;

        // Only navigate if not already on target route
        if (currentPath !== targetPath) {
          onNavigate();
        }
      }

      // Clear upload session and attachments after successful message send
      if (variables.sendMode !== 'digitalMaintenanceCheck' && uploadSessionId) {
        clearUploadSession();
        clearAttachments();

        // Synchronize conversation files and clean up upload session
        // Associate ONLY the pending files (for this message) with the sent message
        const pendingFiles = getPendingFilesForMessage(conversationId);
        if (pendingFiles.length > 0) {

          // Update conversation cache to include files in the specific message we just completed
          queryClient.setQueryData(
            conversationQueryKeys.detail(conversationId),
            (old: any) => {
              if (!old || !old.conversationHistory) return old;

              const updatedHistory = [...old.conversationHistory];
              const targetIndex = updatedHistory.findIndex((m: any) => m.message_id === response.message_id);

              if (targetIndex !== -1) {
                updatedHistory[targetIndex] = {
                  ...updatedHistory[targetIndex],
                  uploadedFiles: pendingFiles,
                };
              }

              return {
                ...old,
                conversationHistory: updatedHistory,
              };
            }
          );

          // Clear the conversation upload session since files are now associated
          clearConversationUploadSession(conversationId);
        }
      }

      // NEW: Clear pending files for message (input indicators) regardless of upload session
      if (variables.sendMode !== 'digitalMaintenanceCheck') clearPendingFilesForMessage(conversationId);

      // Note: Removed invalidation - cache is already updated with real data above
      // No need to refetch since we have the complete, up-to-date conversation data

      // Unified approach: Preserve optimistic updates for all conversations (Clean architecture)
      // Backend needs time to update regardless of conversation type (new creation or timestamp update)
      const isNewConversation = !context.previousConversation ||
        !(context.previousConversation && typeof context.previousConversation === 'object' && 'conversationHistory' in context.previousConversation) ||
        !Array.isArray((context.previousConversation as any)?.conversationHistory) ||
        (context.previousConversation as any)?.conversationHistory?.length === 0;

      if (isNewConversation) {

        // Remove optimistic flag and update with real data from response
        queryClient.setQueryData(
          conversationQueryKeys.list({}),
          (oldLists: any) => {
            if (!oldLists || !Array.isArray(oldLists)) return oldLists;

            return oldLists.map((conv: any) => {
              if (conv.conversationID === conversationId && conv._optimistic) {
                // Update optimistic conversation with real data
                return {
                  ...conv,
                  message_id: response.message_id,
                  // Keep existing title (derived from user message)
                  _optimistic: false, // Remove optimistic flag
                };
              }
              return conv;
            });
          }
        );

      }

      // Unified invalidation strategy: Mark as stale but preserve optimistic updates
      // Natural stale time refresh (30 seconds) will handle eventual consistency for all conversations
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys.list({}),
        refetchType: 'none' // Mark as stale but don't refetch immediately
      });
    },

    // Error handling with rollback
    onError: (error: Error, _variables: SendMessageRequest, context?: OptimisticUpdateContext) => {
      endStream();
      if (FEATURES.showDigitalMaintenanceCheckResult && context) {
        useChatStore.getState().setDigitalMaintenanceCheck(conversationId, context.previousCheck);
      }
      // Handle manual cancellation (AbortError) gracefully
      const isAbortError = error.name === ERROR_NAMES.ABORT ||
                          error.message.includes(ERROR_MESSAGES.ABORTED) ||
                          error.message.includes(ERROR_MESSAGES.CANCELLED) ||
                          (error instanceof ChatApiError && error.code === ERROR_CODES.REQUEST_CANCELLED);

      // Rollback conversation optimistic update
      if (context?.previousConversation) {
        queryClient.setQueryData(
          conversationQueryKeys.detail(conversationId),
          context.previousConversation
        );
      }

      // Rollback conversation list optimistic update
      if (context?.previousLists) {
        queryClient.setQueryData(
          conversationQueryKeys.list({}),
          context.previousLists
        );
      }

      // Rollback selection state optimistic update (Fix Issue 1)
      if (context?.previousSelectedConversationId !== undefined) {
        setSelectedConversation(context.previousSelectedConversationId);
      }

      // Rollback current conversation state optimistic update
      if (context?.previousCurrentConversationId !== undefined) {
        const setCurrentConversationId = useConversationStore.getState().setCurrentConversationId;
        setCurrentConversationId(context.previousCurrentConversationId);
      }

      // End streaming state on any error (including manual cancellation)

      // Log error for debugging (but don't log user cancellations as errors)
      if (!isAbortError) {
        console.error('Failed to send message:', error);
      } else {
        console.log('Message sending cancelled by user');
      }
    },

    // Removed onSettled invalidation for unified approach
    // Natural stale time refresh (30 seconds) handles eventual consistency for all conversations
    // This prevents premature API calls before backend updates are complete

    // Retry configuration - DO NOT retry while actively streaming
    retry: (failureCount: number, error: any) => {
      console.log('🔄 Retry check - failureCount:', failureCount, 'error:', error?.message);

      // Check if we're currently streaming - if so, DO NOT retry
      const streamingState = useStreamingStore.getState();
      if (streamingState.isStreaming) {
        console.log('⛔ Blocking retry - stream is active');
        return false;
      }

      // Don't retry authentication errors
      if (error instanceof ChatApiError && error.status === 401) {
        return false;
      }

      const isAbortError = error.name === ERROR_NAMES.ABORT ||
                    error.message.includes(ERROR_MESSAGES.ABORTED) ||
                    error.message.includes(ERROR_MESSAGES.CANCELLED) ||
                    (error instanceof ChatApiError && error.code === ERROR_CODES.REQUEST_CANCELLED);

      if (isAbortError){
        return false;
      }

      // Don't retry validation errors (4xx)
      if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 501) {
        return false;
      }

      // Only retry network errors and server errors, max 1 time (2 total attempts)
      const shouldRetry = failureCount < 1;
      console.log(shouldRetry ? '✅ Will retry' : '❌ No more retries');
      return shouldRetry;
    },

    // Exponential backoff for retries
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

/**
 * Enhanced hook for sending messages with custom history options
 * Specifically designed for message editing use cases
 */
export function useSendMessageWithCustomHistory(conversationId: string) {
  const queryClient = useQueryClient();
  const sendMessageMutation = useSendMessage(conversationId);

  // Access the chat store for model selection
  const selectedModel = useChatStore(
    useCallback((state) => state.getSelectedModel(conversationId), [conversationId])
  );

  /**
   * Send a message with a custom history configuration
   * @param message - The message text to send
   * @param options - Additional options including history customization
   * @returns Promise with the API response
   */
  const sendMessageWithCustomHistory = useCallback(async (
    message: string,
    options?: {
      group?: string;
      model?: string;
      messageToReplaceId?: string;  // ID of the message being edited
      temperature?: number;
      tools?: any[];
      customHistoryEndIndex?: number; // Index to truncate history at
      currentStreamId?: string; // ID of the message receiving this stream
      abortController?: AbortController; // AbortController for request cancellation
    }
  ) => {

    // await connect(); // Establish WebSocket connection if not already connected
    if (!message.trim()) {
      throw new Error('Message cannot be empty');
    }
    let messageIndex = 0;

    try {
      // Get conversation data from cache
      const conversationData = queryClient.getQueryData<ConversationDetailResponse>(
        conversationQueryKeys.detail(conversationId)
      );

      if (!conversationData?.conversationHistory) {
        throw new Error("Cannot send message: conversation history not found");
      }

      let history = conversationData.conversationHistory;
      // If replacing a specific message, truncate history at that message
      if (options?.messageToReplaceId) {
        // Find the index of the message being edited
        const baseMessageId = options.messageToReplaceId.replace(/-user$/, '');
        messageIndex = history.findIndex(
          (msg: any) => msg.message_id === options.messageToReplaceId ||
            msg.message_id === baseMessageId
        );

        if (messageIndex !== -1) {
          // Keep only history up to the edited message (exclusive)
          history = history.slice(0, messageIndex);
        }
      } else if (options?.customHistoryEndIndex !== undefined) {
        // Alternative: use a specific index to truncate history
        history = history.slice(0, options.customHistoryEndIndex);
      }

      // Transform to backend format
      const formattedHistory = chatUtils.transformHistoryToBackendFormat(history);

      // set uploadSessionId as it is null in the state after deleting the messages
      const conversationDataUploadSessionId = conversationData?.conversationHistory[messageIndex]?.upload_session_id;
      const lastMessageUploadedFiles = conversationData?.conversationHistory[messageIndex]?.uploadedFiles || [];

      // Get current state from chat store
      const chatState = useChatStore.getState();
      const {
        uploadSessionId,
        attachments,
        hasGenieAccess,
        allowedGenieSpaceNames,
      } = chatState;

      // Analyze file state
      const pendingFilesForMessage = chatState.getPendingFilesForMessage(conversationId);
      const conversationFiles = chatState.getConversationFiles(conversationId);
      const fileState = chatUtils.analyzeFileState({
        conversationData,
        uploadSessionId,
        attachments,
        conversationFiles,
        pendingFilesForMessage,
      });

      const currentConversation = queryClient.getQueryData<ConversationDetailResponse>(conversationQueryKeys.detail(conversationId));
      const normalizedGenieIds = ensureDefaultGenieIds(currentConversation?.genie_conversation_ids as Record<string, string> | null);
      const conversationUploadSessionId = chatState.conversationFiles?.[conversationId]?.lastUploadSessionId || '';
      const effectiveUploadSessionId =
        (conversationDataUploadSessionId && conversationDataUploadSessionId.length > 0)
          ? conversationDataUploadSessionId
          : (fileState.uploadSessionId || (pendingFilesForMessage.length > 0 ? conversationUploadSessionId : ''));

      // Determine which Genie spaces to query based on agent selection
      // Backend handles all permission checks
      let genieSpacesToQuery = allowedGenieSpaceNames;

      // Check if user explicitly selected a Genie space agent (from options if provided)
      const selectedAgents = options?.tools || []; // tools might contain agent selection
      const explicitlySelectedGenieSpace = getExplicitGenieSpaceSelection(selectedAgents);
      if (explicitlySelectedGenieSpace) {
        genieSpacesToQuery = [explicitlySelectedGenieSpace];
      }

      const effectiveModel = options?.model || selectedModel || DEFAULT_MODEL;

      // Send the message with the custom history
      const response = await sendChatMessage({
        conversationId,
        message: message.trim(),
        history: formattedHistory,
        currentMessageUploadedFiles: lastMessageUploadedFiles, // Attach the files that belong to the message being edited
        abortController: options?.abortController, // Pass AbortController if provided
        options: {
          model: effectiveModel,
          temperature: options?.temperature,
          tools: options?.tools,
          conversationTitle: chatUtils.getConversationTitle(history, message),
          uploadSessionId: effectiveUploadSessionId,
          useFiles: fileState.shouldUseFiles,
          uploadedFiles: fileState.allFiles,
          currentStreamId: options?.currentStreamId,
          // Include Genie access information:
          hasGenieAccess,
          genieSpaceNames: genieSpacesToQuery,
          // TODO: See previous TODO in other sendChatMessage.
          genieSpaceConversationIds: normalizedGenieIds,
        }
      });

      return response;
    } catch (error: any) {
  const isAbortError =
    error?.name === ERROR_NAMES.ABORT ||
    (typeof error?.message === 'string' && (error.message.includes(ERROR_MESSAGES.ABORTED) || error.message.includes(ERROR_MESSAGES.CANCELLED))) ||
    (error instanceof ChatApiError && error.code === ERROR_CODES.REQUEST_CANCELLED);
  if (!isAbortError) {
    console.error('Failed to send message with custom history:', error);
  } else {
    // Silently handle user cancellation to avoid noisy logs
  }
  throw error;
}
  }, [conversationId, queryClient, selectedModel]);

  return {
    sendMessageWithCustomHistory,
    isLoading: sendMessageMutation.isPending,
    error: sendMessageMutation.error,
    reset: sendMessageMutation.reset,
    canSend: !sendMessageMutation.isPending,
  };
}

/**
 * High-level hook that integrates mutation with chat store
 * Provides clean interface for components
 */
export function useChatActions(conversationId: string, onNavigate?: () => void) {
  const sendMessageMutation = useSendMessage(conversationId, onNavigate);
  const queryClient = useQueryClient();

  // Store AbortController reference at this level
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(async (
    message: string,
    options?: SendMessageRequest['options'],
    sendMode?: SendMessageRequest['sendMode']
  ) => {
    if (!message.trim()) {
      throw new Error('Message cannot be empty');
    }
    if (FEATURES.showDigitalMaintenanceCheckResult && (isChatSendInFlight(conversationId) || queryClient.getMutationCache().getAll().some(
      mutation => mutation.state.status === 'pending' && mutation.options.meta?.type === 'chat'
    ))) {
      throw new Error('A chat message is already being sent');
    }
    activeChatSends.add(conversationId);

    try {
      // Create AbortController for this request
      abortControllerRef.current = new AbortController();

      const response = await sendMessageMutation.mutateAsync({
        conversationId,
        message: message.trim(),
        options,
        sendMode,
        abortController: abortControllerRef.current,
      });

      return response;
    } catch (error) {
      // Error is handled by mutation's onError
      throw error;
    } finally {
      // Clean up AbortController reference
      abortControllerRef.current = null;
      activeChatSends.delete(conversationId);
    }
  }, [conversationId, queryClient, sendMessageMutation]);

  // Expose abort functionality
  const abortRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Filter out AbortError from being displayed in UI
  const displayError = useMemo(() => {
    const error = sendMessageMutation.error;
    if (!error) return null;

    const isAbortError = error.name === ERROR_NAMES.ABORT ||
                        error.message.includes(ERROR_MESSAGES.ABORTED) ||
                        error.message.includes(ERROR_MESSAGES.CANCELLED) ||
                        (error instanceof ChatApiError && error.code === ERROR_CODES.REQUEST_CANCELLED);

    return isAbortError ? new Error(CONNECTION_CANCELLED_MESSAGE) : error;
  }, [sendMessageMutation.error]);

  return {
    sendMessage,
    isLoading: sendMessageMutation.isPending,
    error: displayError, // Use filtered error instead of raw error
    isSuccess: sendMessageMutation.isSuccess,
    isError: sendMessageMutation.isError && !displayError, // Only show error state if we have a displayable error
    reset: sendMessageMutation.reset,
    abortRequest, // Expose abort functionality
    // Helper computed properties
    canSend: !sendMessageMutation.isPending,
  };
}


/**
 * Hook for managing message sending state with user feedback
 */
export function useMessageSendingState(conversationId: string) {
  const { sendMessage, isLoading, error, isSuccess, isError, reset } = useChatActions(conversationId);

  const sendMessageWithFeedback = useCallback(async (
    message: string,
    options?: SendMessageRequest['options']
  ) => {
    try {
      await sendMessage(message, options);
      return { success: true, error: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send message'
      };
    }
  }, [sendMessage]);

  return {
    sendMessage: sendMessageWithFeedback,
    isLoading,
    error,
    isSuccess,
    isError,
    reset,
    canSend: !isLoading,
  };
}

/**
 * Hook for optimistic message management
 * Provides utilities for managing optimistic updates
 */
export function useOptimisticMessages(conversationId: string) {
  const queryClient = useQueryClient();

  const addOptimisticMessage = useCallback((message: any) => {
    queryClient.setQueryData(
      conversationQueryKeys.detail(conversationId),
      (old: any) => {
        if (!old) return old;

        const newChatMessage = {
          user: message.content,
          bot: message.role === 'assistant' ? message.content : '',
          message_id: message.id,
          tools: message.metadata?.tools || [],
          temperature: message.metadata?.temperature || 0,
          gptVersion: message.metadata?.model || DEFAULT_MODEL,
        };

        return {
          ...old,
          conversationHistory: [...old.conversationHistory, newChatMessage],
        };
      }
    );
  }, [conversationId, queryClient]);

  const removeOptimisticMessage = useCallback((messageId: string) => {
    queryClient.setQueryData(
      conversationQueryKeys.detail(conversationId),
      (old: any) => {
        if (!old) return old;

        return {
          ...old,
          conversationHistory: old.conversationHistory.filter(
            (msg: any) => msg.message_id !== messageId
          ),
        };
      }
    );
  }, [conversationId, queryClient]);

  const updateOptimisticMessage = useCallback((messageId: string, updates: any) => {
    queryClient.setQueryData(
      conversationQueryKeys.detail(conversationId),
      (old: any) => {
        if (!old) return old;

        return {
          ...old,
          conversationHistory: old.conversationHistory.map((msg: any) => {
            if (msg.message_id === messageId) {
              return { ...msg, ...updates };
            }
            return msg;
          }),
        };
      }
    );
  }, [conversationId, queryClient]);

  return {
    addOptimisticMessage,
    removeOptimisticMessage,
    updateOptimisticMessage,
  };
}
